import React, { useState, useEffect, useRef } from 'react';
import { 
  AcademicCapIcon, 
  PlusIcon, 
  PencilIcon,
  TrashIcon,
  ChevronDownIcon, ChevronUpIcon
} from '@heroicons/react/24/outline';

import { checkApi } from '../../Authentication/AxiosInstance';
import { DEFAULT_ACADEMIC_YEAR } from '../../../config/constants';
import SDExamAddForm from './SDExamAddForm';
import SDExamEditForm from './SDExamEditForm';


const SDExam = ({ 
  candidateId,
  setRefreshExamList,
  refreshExamList,
}) => {
  const [examsMaster, setExamsMaster] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true); // Profile section expanded by default
  const [candidateExams, setCandidateExams] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingExam, setEditingExam] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
     
  //Populate candidateExams with candidate-exams data
  const fetchCandidateExams = async () => {
    if (!candidateId) return;
    try {
      setLoading(true);
      // Use the my_exams endpoint that includes phase information
      const response = await checkApi.post(`/candidates/candidate-exams/my_exams/`, {
        cndt_academicyear: DEFAULT_ACADEMIC_YEAR
      });
      setCandidateExams(response.data);
    } catch (error) {
      console.error('Error fetching candidate exams:', error);
    } finally {
      setLoading(false);
    }
  };

  
  const fetchExamsMaster = async () => {
    try {
      const response = await checkApi.get("/masters/exammaster/");
      setExamsMaster(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };
  
  
  // Handle Add Exam Operations - Change Exam, Save, Cancel.
  const handleAddExam = () => {
    setIsEditing(false);
    setIsAdding(true);
    setEditingExam({});
    setError('');
    setSuccess('');
  };


  const onSubmit = async (formData) => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      console.log('DEBUG: Form data:', formData);
      console.log('DEBUG: candidateId being sent:', candidateId);
      
      const mapped = {
        cndt_id: candidateId,
        cndt_exammaster_id: formData.cndt_exam_id || null, // Map form field to correct backend field
        cndt_academicyear: formData.cndt_academicyear || null,
        cndt_gender: formData.cndt_gender || null,
        cndt_category: formData.cndt_category || null,
        // Map special categories to individual boolean columns
        cndt_is_pwd: formData.candidate_exam_special_category?.includes('PWD') || null,
        cndt_is_def: formData.candidate_exam_special_category?.includes('DEF') || null,
        cndt_is_orphan: formData.candidate_exam_special_category?.includes('ORPHAN') || null,
        cndt_is_tfws: formData.candidate_exam_special_category?.includes('TFWS') || null,
        cndt_is_ews: formData.candidate_exam_special_category?.includes('EWS') || null,
        // Include rank fields for both create and update
        cndt_exam_airank: formData.cndt_exam_airank || null,
        cndt_exam_strank: formData.cndt_exam_strank || null
      };

      // Remove undefined keys so we don't send them, but keep null values
      const examData = Object.fromEntries(
        Object.entries(mapped).filter(([_, v]) => v !== undefined)
      );
      
      if (editingExam.id) {
        // Use the profile update endpoint for exam updates to ensure gender/category are saved
        const candidateExamUpdateData = {
          section_type: 'exam',
          ...examData
        };
        await checkApi.put('/candidates/candidate-details/update_profile/', candidateExamUpdateData);
        setSuccess('Exam updated successfully!');
      } else {
        await checkApi.post('/candidates/candidate-exams/', examData);
        setSuccess('Exam added successfully!');
      }

      setIsEditing(false);
      setIsAdding(false);
      setEditingExam({});
      setRefreshExamList(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      
      console.log(err)
      console.error('Save exam error:', err?.response?.data || err?.message);
      setError(err.response?.data?.duplicate_entry || 'Failed to save exam. Please check your inputs and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsAdding(false);
    setEditingExam({});
    setError('');
    setSuccess('');
  };

  // Handle Edit and Delete Exam from inside the Exam Tracker Card
  const handleEditExam = (exam) => {
    console.log('Editing exam:', exam);

    // Map backend data to frontend format
    const specialCategories = [];
    if (exam.is_pwd) specialCategories.push('PWD');
    if (exam.is_def) specialCategories.push('DEF');
    if (exam.is_orphan) specialCategories.push('ORPHAN');
    if (exam.is_tfws) specialCategories.push('TFWS');
    if (exam.is_ews) specialCategories.push('EWS');

    // Set editing exam data
    
    setEditingExam({"id":exam.cndt_exammaster_id,"name":exam.exam_name,"board":exam.exam_board,"session":exam.exam_session});
    setIsEditing(true);
    setIsAdding(false);
    setError('');
    setSuccess('');

  };
  const handleDeleteExam = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;

    try {
      await checkApi.delete(`/candidates/candidate-exams/${examId}/`, {
        data: {
          cndt_academicyear: DEFAULT_ACADEMIC_YEAR
        }
      });
      setSuccess('Exam deleted successfully!');
      setRefreshExamList(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.log(err)
      setError('Failed to delete exam. Please try again.');
    }
  };


  // Exam Tracker Card Start
  function ExamTrackerCard({ exam, onView, onEdit, onDelete }) {
    return (
      <div className="rounded-xl shadow bg-white h-auto">
          {/* Header (compact) */}
          <div className="px-4 py-2 border-b border-pink-500 rounded-t-xl">
            <div className="bg-white flex items-center justify-between">
                <h3 className="text-pink-500 font-semibold text-sm truncate items-center gap-2">
                    {exam.exam_name.toUpperCase()}
            </h3>
            <div className="flex items-center gap-1">
              <button onClick={() => handleEditExam(exam)} 
                  className="p-1 rounded hover:bg-pink-500" title="Edit">
                <PencilIcon className="h-4 w-4 text-pink-500 hover:text-white hover:font-bold" />
              </button>
              <button onClick={() => handleDeleteExam(exam.id)} 
                className="p-1 rounded hover:bg-white" title="Delete">
                <TrashIcon className="h-4 w-4 text-red-500" />
              </button>
                </div>
            </div>
          </div>

          {/* Body (compact) */}
          <div className="p-4 space-y-2 text-sm">
              {/* Gender */}
              {(() => {
                    const selectedGender = exam.gender?.find(g => g.selected)?.gender_description 
                                        || exam.gender_details?.gender_description 
                                        || exam.gender_description 
                                        || 'NA';
                    return selectedGender != 'NA' && selectedGender != null && (
                      <div className="grid grid-cols-2 gap-1">
                      <span className="text-gray-600">Gender:</span>
                      <span className="p-1 rounded-full bg-[#d5b3fc] text-gray-900 text-center">
                            {selectedGender}
                      </span>
                      </div>
                    );
              })()}
              {/* Category */}
              {(() => {
                    const selectedCategory = exam.category?.find(c => c.selected)?.category_abbrev 
                                        || exam.category_details?.category_abbrev 
                                        || exam.category_abbrev 
                                        || 'NA';
                    return selectedCategory != 'NA' && selectedCategory != null && (
                      <div className="grid grid-cols-2 gap-1">
                          <span className="text-gray-600">Category:</span>
                          <span className="p-1 rounded-full text-gray-900 bg-blue-200 text-center">
                                {selectedCategory}
                          </span>
                      </div>
                    ) 
              })()}
              {/* Special Categories */}
              {(() => {
                    const specialCategories = [];
                    if (exam.is_pwd) specialCategories.push('PWD');
                    if (exam.is_ews) specialCategories.push('EWS');
                    if (exam.is_def) specialCategories.push('DEF');
                    if (exam.is_tfws) specialCategories.push('TFWS');
                    if (exam.is_orphan) specialCategories.push('ORPHAN');
                    if (specialCategories.length > 0) {
                      return (
                        <div className="grid grid-cols-2 gap-1">
                        <span className="text-gray-600 col-span-full">Special:</span>
                        {specialCategories.map(category => (
                        <span key={category} className="px-2 py-0.5 rounded-full bg-[#FFCC80] text-gray-900">
                          {category}
                        </span>
                        ))}
                        </div>
                      )}
              })()}
          </div>
          <div className="p-4 space-y-3 text-sm">
            {/* Results Section - Show if results exist */}
            {(exam.strank || exam.airank) && (
              <div className="bg-pink-50 rounded-lg p-1">
                  <div className="grid grid-cols-1 gap-2 p-2">
                      {exam.airank && (
                        <div className="inline-block space-x-2">
                            <span className="text-pink-600 font-bold text-base">All India Rank:</span>
                            <span className="font-bold tracking-wider">{exam.airank}</span>
                        </div>
                      )}
                      {/* State Rank - Only show for MHTCET exam */}
                      {exam.strank && exam.exam_name === 'MHTCET' && (
                          <div className="flex justify-between items-center">  
                              <span className="text-pink-600 font-bold text-base">State Rank:</span>
                              <span className="font-bold tracking-wider">{exam.strank}</span>
                          </div>
                      )}
                  </div>
              </div>
            )}
          </div>
        </div>
      )
  }
  // Exam Tracker Card End

// Carousel Functions Start
const [activeIdx, setActiveIdx] = useState(0);
// Responsive config
const getConfig = () => {
  if (window.innerWidth >= 1024) return { cols: 3, visible: 3 };
  return { cols: 1, visible: 1 };
};
const cr_config = getConfig();
const maxIdx = Math.max(0, Math.ceil(candidateExams.length / cr_config.visible) - 1);
// Touch handlers
const touch = useRef({ startX: 0, dx: 0 });
const onTouchStart = (e) => {
  touch.current.startX = e.touches[0].clientX;
};
const onTouchMove = (e) => {
  touch.current.dx = e.touches[0].clientX - touch.current.startX;
};
const onTouchEnd = () => {
  const threshold = 60;
  if (touch.current.dx < -threshold && activeIdx < maxIdx) {
    setActiveIdx(prev => Math.min(prev + 1, maxIdx));
  } else if (touch.current.dx > threshold && activeIdx > 0) {
    setActiveIdx(prev => Math.max(prev - 1, 0));
  }
};
// Use current-year exams for carousel calculations
const numSlidesCY = Math.max(1, Math.ceil(candidateExams.length / cr_config.visible));
// Carousel Functions End

useEffect(() => {
  fetchCandidateExams();
}, [candidateId, refreshExamList]);

useEffect(() => {
  fetchExamsMaster();
}, []);

  console.log(candidateExams);
console.log('Editing exam:', editingExam);
// Get current exam data for form
const currentExam = editingExam.id ?   candidateExams.find(exam => exam.cndt_exammaster_id === editingExam.id) :   null;

  return (
      <div className="rounded-lg shadow-md shadow-pink-200">
          <div className="bg-tertiary-600 rounded-xl">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between align-middle 
                            shadow-md shadow-pink-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-pink-400 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 space-x-2">
                    {'My Exams and Results'} ({DEFAULT_ACADEMIC_YEAR})
                  </h3>
          </div>
              <div className="flex items-center space-x-2">
                  {/* Add Exams Button */}
                  {!isEditing && !isAdding && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleAddExam}
                            className="flex items-center space-x-2 px-4 py-2 bg-pink-400 
                                    text-white rounded-lg hover:bg-pink-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Exam</span>
            </button>
          </div>
                  )}
                  {/* Collapse/Expand Button */}
                  {!isAdding && !isEditing && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="flex items-center justify-center w-10 h-10 bg-pink-400 text-white rounded-lg hover:bg-pink-800 transition-colors"
                      title={isExpanded ? "Collapse" : "Expand"}
                    >
                      {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5" />
                      ) : (
                          <ChevronDownIcon className="w-5 h-5" />
                      )}
                    </button>
                  )}
              </div>
          </div>
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      {/* Exam List */}
            {isExpanded && !isEditing && !isAdding && (
              <div className="space-y-4 p-2">
                {loading ? (
                  <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading exams...</p>
                  </div>
                ) : (
                      <div className = 'mt-4'>
                          {/* Carousel Container */}
                          <div 
                              className="relative overflow-hidden"
                              onTouchStart={onTouchStart}
                              onTouchMove={onTouchMove}
                              onTouchEnd={onTouchEnd}
                            >
                                    {/* Exam Tracker Cards Container */}
                              <div
                                className="flex transition-transform duration-300 ease-out"
                                style={{ 
                                  transform: `translateX(-${activeIdx * 100}%)`
                                }}
                              >
                                      {/* Exam Tracker Cards Display */}
                                      {console.log('candidateExams:', candidateExams)}
                                      {Array.from({ length: Math.ceil(candidateExams.length / cr_config.visible) }).map((_, batchIdx) => (
                                      <div key={batchIdx} className="w-full shrink-0">
                                        <div className={`grid gap-4 ${cr_config.cols === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
                                          {candidateExams
                                            .slice(batchIdx * cr_config.visible, (batchIdx + 1) * cr_config.visible)
                                            .map((exam) => { 
                                                return <ExamTrackerCard
                                                key={exam.id}
                                                exam={exam}
                                                onEdit={handleEditExam}
                                                onDelete={handleDeleteExam}
                                                onView={() => {/* open details */}}
                                                className="shrink-0 w-[32%] px-2" 
                                          />
                                        })}
                              </div>
                          </div>
                                    ))}
                              </div>
                              {/* Navigation Dots */}
                              <div className="mt-3 flex justify-center gap-2">
                                      {Array.from({ length: numSlidesCY }).map((_, i) => (
                                  <button
                                    key={i}
                                    onClick={() => setActiveIdx(i)}
                                    className={`h-2 w-2 rounded-full ${i === activeIdx ? 'bg-gray-800' : 'bg-gray-300'}`}
                                    aria-label={`Go to slide ${i + 1}`}
                                  />
                                ))}
                              </div>
                          </div>
                          <button disabled={activeIdx===0} onClick={() => setActiveIdx(i=>i-1)} 
                                        className="absolute left-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-white/80 rounded">‹</button>
                          <button disabled={activeIdx===numSlidesCY-1} onClick={() => setActiveIdx(i=>i+1)} 
                                        className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-white/80 rounded">›</button>
                      </div>
                )}
              </div>
            )}
            {/* Exam Add Form */}
            {isExpanded && isAdding && examsMaster.length > 0 && (
              <SDExamAddForm
                examData={null}
                examOptions={examsMaster}
                onSubmit={onSubmit}
                onCancel={handleCancel}
                isSaving={isSaving}
              />
            )}
            {/* Exam Edit Form */}
            {isExpanded && isEditing && examsMaster.length > 0 && (
              <SDExamEditForm
                examData={currentExam}
                examOptions={examsMaster}
                onSubmit={onSubmit}
                onCancel={handleCancel}
                isSaving={isSaving}
              />
            )}
          </div>
    </div>
  );
};

export default SDExam;